import type { PaymentFlow } from './types'

const field = (source: unknown, key: string): unknown => {
  if (typeof source !== 'object' || source === null) return undefined
  return (source as Record<string, unknown>)[key]
}

const stringField = (source: unknown, key: string): string | undefined => {
  const value = field(source, key)
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Centralized dual-read of the 3DS action token (PRO-450). Checks, in
 * order:
 *   1. `data.threeDSecureActionTokenId` — the PRO-443 backend flag delivers
 *      the token here on createOrder; updateBillingInfo already does.
 *   2. top-level `threeDSecureActionTokenId` — usePurchase's legacy synthesis
 *      output (it rescues the token from the thrown error into this shape).
 *   3. `extra.info.transactionError.threeDSecureActionTokenId` on a raw
 *      thrown/API error — **purchase flow only**: billing-info responses
 *      already carry the token in the success payload, and rescuing from a
 *      billing-info error would leave the mutation in `isError` while the
 *      challenge runs, firing error feedback mid-challenge.
 *
 * Kushki/DLocal responses carry none of these fields, so this returns
 * undefined for them by construction.
 */
export function extract3dsActionToken(
  resultOrError: unknown,
  flow: PaymentFlow,
): string | undefined {
  const fromData = stringField(field(resultOrError, 'data'), 'threeDSecureActionTokenId')
  if (fromData) return fromData

  const fromTopLevel = stringField(resultOrError, 'threeDSecureActionTokenId')
  if (fromTopLevel) return fromTopLevel

  if (flow !== 'purchase') return undefined

  const transactionError = field(field(field(resultOrError, 'extra'), 'info'), 'transactionError')
  return stringField(transactionError, 'threeDSecureActionTokenId')
}
